"use client";

import { Settings } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  Divider,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
} from "@mui/material";
import type { Table } from "@tanstack/react-table";
import * as React from "react";

type DataTableViewOptionsProps<TData> = {
  table: Table<TData>;
};

export function DataTableViewOptions<TData>({ table }: DataTableViewOptionsProps<TData>) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const columns = table
    .getAllColumns()
    .filter((column) => typeof column.accessorFn !== "undefined" && column.getCanHide());

  return (
    <>
      <Button
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size="small"
        startIcon={<Settings fontSize="small" />}
        sx={{
          display: { xs: "none", lg: "inline-flex" },
          textTransform: "none",
          whiteSpace: "nowrap",
        }}
        variant="outlined"
      >
        Columns
      </Button>

      <Menu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        open={open}
        slotProps={{
          paper: {
            sx: {
              minWidth: 220,
              maxWidth: 280,
              borderRadius: 2,
            },
          },
        }}
      >
        <ListSubheader sx={{ fontSize: "0.875rem", fontWeight: 600 }}>Show Columns</ListSubheader>
        <Divider />

        {columns.length === 0 ? (
          <MenuItem disabled sx={{ py: 2, justifyContent: "center" }}>
            No columns available
          </MenuItem>
        ) : (
          columns.map((column) => (
            <MenuItem
              key={column.id}
              onClick={() => column.toggleVisibility(!column.getIsVisible())}
              sx={{
                pl: 1,
                pr: 1.5,
                gap: 1,
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Checkbox
                checked={column.getIsVisible()}
                size="small"
                sx={{
                  p: 0.5,
                }}
              />
              <ListItemText
                primary={column.columnDef.meta?.label ?? column.id}
                sx={{
                  m: 0,
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
